import React, { useState, useEffect } from 'react'
import { mainApi } from '../../../../store/services/mainApi'
import {
  MenuItem,
  Modal,
  Box,
  CircularProgress,
  Typography,
  Switch,
  Button,
  Select,
} from '@mui/material'
import { useMediaQuery } from '@mui/material'
import { ArrowLeft } from '@mui/icons-material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'

import './FastOrder.scss'
import { useDispatch } from 'react-redux'
import useSendLog from '../../../../hooks/useSendLog'
import { setItemsCart } from '../../../../store/slices/allItemsCart'
import { useNavigate, useParams } from 'react-router'
import useUpdatePrice from '../../../../hooks/useUpdatePrice'

export default function FastOrder({ tradeNames, contactID, sxBtn }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [fastOrderModal, setFastOrderModal] = useState(false)
  const [showPhotos, setShowPhotos] = useState(false)
  const [selectedItems, setSelectedItems] = useState([])
  const [updatedFastOrderData, setUpdatedFastOrderData] = useState([])

  // Запрос полного каталога (аналогично FastTempleOrder)
  const { data: allProductsData, isLoading: isAllProductsLoading } =
    mainApi.useFindAllWordsQuery({ pollingInterval: 50000 })

  // 1️⃣ Запрос товаров быстрого заказа должен быть ПЕРЕД useEffect, который на него опирается
  const {
    data: fastOrderData,
    isLoading: fastOrderLoading,
    isError: fastOrderError,
    isFetching: fastOrderFetching,
  } = mainApi.useGetProductsBySkusQuery(
    { skus: tradeNames ? tradeNames.join(',') : '' },
    { skip: !fastOrderModal, refetchOnMountOrArgChange: true }
  )

  // 2️⃣ Эффект обновления цен – теперь fastOrderData точно определён
  useEffect(() => {
    if (!isAllProductsLoading && allProductsData && fastOrderData) {
      const updated = useUpdatePrice(
        fastOrderData.map(item => ({ ...item })),
        allProductsData,
        isAllProductsLoading
      )
      setUpdatedFastOrderData(updated)
    }
  }, [fastOrderData, allProductsData, isAllProductsLoading])

  // 3️⃣ Синхронизируем цены selectedItems при изменении updatedFastOrderData
  useEffect(() => {
    if (updatedFastOrderData.length > 0 && selectedItems.length > 0) {
      setSelectedItems(prev =>
        prev.map(selectedItem => {
          const fresh = updatedFastOrderData.find(i => i._id === selectedItem._id)
          return fresh ? { ...selectedItem, ...fresh, quantity: selectedItem.quantity, quantityOrder: selectedItem.quantityOrder } : selectedItem
        })
      )
    }
  }, [updatedFastOrderData])

  const handleFastOrder = () => setFastOrderModal(true)
  const handleCloseFastOrder = () => setFastOrderModal(false)

  const handleSelectUnit = (item, event) => {
    setSelectedItems(prev =>
      prev.map(i => (i._id === item._id ? { ...i, currentUnit: event.target.value } : i))
    )
  }

  const incrementQuantity = (item) => {
    const existing = selectedItems.find(i => i._id === item._id)
    if (existing) {
      setSelectedItems(prev =>
        prev.map(i =>
          i._id === item._id
            ? { ...i, quantityOrder: i.quantityOrder + 1, quantity: i.quantity + 1 }
            : i
        )
      )
    } else {
      const freshItem = updatedFastOrderData.find(i => i._id === item._id) || item
      setSelectedItems(prev => [...prev, { ...freshItem, quantityOrder: 1, quantity: 1 }])
    }
  }

  const decrementQuantity = (item) => {
    setSelectedItems(prev =>
      prev
        .map(i => {
          if (i._id === item._id) {
            const newQty = i.quantityOrder > 1 ? i.quantityOrder - 1 : 0
            return newQty > 0 ? { ...i, quantityOrder: newQty, quantity: newQty } : null
          }
          return i
        })
        .filter(Boolean)
    )
  }

  const isSalePage = location.pathname.includes('/group')
  const tradeNameParams = useParams().tradeName
  const platformParam = useParams().platform

  const handleCreateOrder = async () => {
    dispatch(
      setItemsCart(
        selectedItems
          .map(el => ({ ...el }))
          .filter(el => el.sku !== 'BBroCILtar100')
      )
    )
    if (!isSalePage) {
      if (tradeNameParams !== undefined) {
        const path = platformParam
          ? `/newOrder/${platformParam}/${tradeNameParams}`
          : `/newOrder/${tradeNameParams}`
        navigate(path)
        window.scrollTo(0, 0)
      }
    } else {
      navigate('/group/newOrder')
    }
    dispatch(mainApi.util.invalidateTags(['ShipmentOrders']))
    handleCloseFastOrder()
  }

  // Показываем лоадер, пока данные каталога или товаров не готовы, либо обновлённые данные ещё пусты
  const isDataReady = updatedFastOrderData.length > 0

  return (
    <Box>
      <Button size='small' onClick={handleFastOrder} variant='contained' sx={sxBtn}>
        Repetir 🔁
      </Button>

      <Modal open={fastOrderModal} onClose={handleCloseFastOrder}>
        <Box
          className='cartModal'
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 350,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            maxHeight: '500px',
            overflowY: 'auto',
            fontFamily: 'Montserrat',
          }}
        >
          <div className='cartModal__header'>
            <div className='cartModal__header__back'>
              <ArrowLeft color='success' />
              Back
            </div>
            <div className='cartModal__header__proMod'>
              <Typography sx={{ fontFamily: 'Montserrat' }}>Pro Mode (W/O Photo)</Typography>
              <Switch value={showPhotos} onChange={() => setShowPhotos(!showPhotos)} />
            </div>
          </div>

          <div className='cartModal__body'>
            {fastOrderFetching || !isDataReady ? (
              <CircularProgress />
            ) : (
              updatedFastOrderData.map(item => (
                <div key={item._id} className='cartModal__body__item'>
                  <div className='cartModal__body__item__row'>
                    <img
                      src={item.img}
                      width='50px'
                      height='50px'
                      alt='item'
                      style={{ display: showPhotos ? 'block' : 'none' }}
                    />
                    <div className='cartModal__body__item__row__info'>
                      {item.name}
                      <div className='cartModal__body__item__row__info__sku'>{item.sku}€</div>
                    </div>
                  </div>
                  <div className='cartModal__body__item__row'>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          border: '1px solid #ccc',
                          padding: '5px',
                          borderRadius: '32px',
                          backgroundColor: '#f5f4f2',
                          zIndex: 1,
                        }}
                      >
                        <RemoveOutlinedIcon
                          onClick={() => decrementQuantity(item)}
                          sx={{ color: '#5fc56e', cursor: 'pointer' }}
                        />
                        <span style={{ color: '#5fc56e' }}>
                          {selectedItems.find(i => i._id === item._id)?.quantity || 0}
                        </span>
                        <AddOutlinedIcon
                          onClick={() => incrementQuantity(item)}
                          sx={{ color: '#5fc56e', cursor: 'pointer' }}
                        />
                      </Box>
                      <Box
                        sx={{
                          border: '1px solid #ccc',
                          borderRadius: '32px',
                          borderTopLeftRadius: '0px',
                          borderBottomLeftRadius: '0px',
                          height: '32px',
                          marginLeft: '-10px',
                          paddingLeft: '25px',
                          paddingRight: '15px',
                          borderLeft: '0px',
                          backgroundColor: '#5fc56e',
                          alignItems: 'center',
                          display: 'flex',
                          justifyContent: 'center',
                          color: '#fff',
                          position: 'relative',
                        }}
                      >
                        {item.unit.includes(',') ? (
                          <Select
                            value={
                              selectedItems.find(i => i._id === item._id)?.currentUnit ||
                              item.unit.split(',')[0]
                            }
                            onChange={event => handleSelectUnit(item, event)}
                            IconComponent={KeyboardArrowDownOutlinedIcon}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                            variant='standard'
                            inputProps={{ style: { fontFamily: 'Montserrat', fontSize: '12px' } }}
                            disableUnderline
                            sx={{
                              color: '#fff',
                              '& .MuiSvgIcon-root': { color: '#fff' },
                              '& .MuiInputBase-root': {
                                '&&&:before': { borderBottom: 'none' },
                                '&&:after': { borderBottom: 'none' },
                              },
                            }}
                          >
                            {item.unit.split(',').map(weight => (
                              <MenuItem key={weight} value={weight}>
                                {weight}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          item.unit
                        )}
                      </Box>
                    </Box>
                    <span>{item.marketPriceCP} €</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <Box className='cartModal__footer'>
            <div className='cartModal__footer__total'>
              Total{' '}
              {selectedItems
                .reduce((acc, item) => acc + (item.marketPriceCP || 0) * (item.quantity || 1), 0)
                .toFixed(2)}
              €
              <Button
                onClick={handleCreateOrder}
                className='cartModal__footer__total__btn'
                sx={{ width: '230px' }}
                variant='contained'
                color='success'
              >
                Repetir order
              </Button>
            </div>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}